<template>
    <div class="p-2">
        <back-to-dashboard />

        <b-alert
            v-if="onboardingUser.creatorDisableRequest"
            show
            variant="warning"
        >
            <font-awesome-icon
                icon="fa-duotone fa-triangle-exclamation"
                size="lg"
            />
            {{
                $t(
                    "Requests are currently disabled, you may miss potential earnings."
                )
            }}
            <div class="mt-3">
                {{
                    $t(
                        "Click Enable Requests to turn requests back on and increase your earnings"
                    )
                }}
            </div>
            <b-btn
                @click="enableRequests"
                variant="primary"
                pill
                class="mt-3"
                block
                size="lg"
            >
                <h3 class="mb-0">
                    {{ $t("Enable Requests") }}
                </h3>
                <div class="small font-weight-bold">
                    {{ $t("Increase your earnings") }}
                </div>
            </b-btn>
        </b-alert>

        <div class="d-flex align-items-center justify-content-between">
            <h5 class="mb-0">{{ $t("Requests") }}</h5>
            <b-btn
                @click="guideModal = true"
                variant="light"
                pill
                size="sm"
                class="border"
            >
                <font-awesome-icon
                    icon="fa-duotone fa-info-circle"
                    class="mr-1"
                />
                {{ $t("Guide") }}
            </b-btn>
        </div>

        <div v-if="creatorRequests" class="mt-2">
            <div
                v-if="creatorRequests.length === 0"
                class="text-center py-5"
            >
                <font-awesome-icon
                    icon="fa-duotone fa-comment-dollar"
                    size="3x"
                    class="branded mb-3"
                />
                <h5>{{ $t("No requests yet") }}</h5>
                <div class="text-secondary">
                    {{
                        $t(
                            "Customers can send you paid content requests right from your profile or inside chat. When they do, they'll show up here for you to accept or reject."
                        )
                    }}
                </div>
                <b-btn
                    variant="primary"
                    pill
                    class="mt-3"
                    @click="guideModal = true"
                >
                    <font-awesome-icon
                        icon="fa-duotone fa-info-circle"
                        class="mr-1"
                    />
                    {{ $t("How requests work") }}
                </b-btn>
                <div class="mt-3 small">
                    <b-link to="/creator/settings" class="text-secondary">
                        {{ $t("Don't want to receive requests? Disable in Settings") }}
                    </b-link>
                </div>
            </div>
            <div
                v-else
                :key="creatorRequest.id"
                v-for="creatorRequest in creatorRequests"
                @click="
                    $router.push({
                        name: 'creatorRequestManage',
                        params: { id: creatorRequest.id },
                    })
                "
            >
                <user-standard-avatar
                    :timestamp="creatorRequest.created"
                    :standardMap="creatorRequest.user"
                    :nonPrimaryPhotos="false"
                    :hideBadges="true"
                    :userBetweenStatus="false"
                    size="small"
                    class="border-light border-top"
                >
                    <div class="d-flex align-items-center mt-1">
                        <coin-badge class="mr-2" :amount="creatorRequest.payout" />

                        <creator-request-status
                            :creatorRequest="creatorRequest"
                        />
                    </div>
                </user-standard-avatar>
            </div>
        </div>

        <b-modal
            v-model="guideModal"
            hide-footer
            centered
            title="How Requests Work"
        >
            <div class="mb-3">
                <strong>{{ $t("What are requests?") }}</strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "Customers can offer you credits to create custom content for them. You decide what to make, how to deliver it, and what you're comfortable with."
                        )
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-comment-dollar"
                        class="mr-1 branded"
                    />
                    {{ $t("How customers send them") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "There's a request button on your profile and inside your chats. When a customer submits one, it shows up here for you to accept or reject."
                        )
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-hand"
                        class="mr-1 branded"
                    />
                    {{ $t("You're in total control") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "You're never forced to do anything. If you don't like a request, just reject it — no penalty, no explanation needed. You can also turn requests off completely in Settings."
                        )
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-magnifying-glass"
                        class="mr-1 branded"
                    />
                    {{ $t("Read each request carefully") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "Make sure you understand exactly what the customer is asking for before you accept. Customers can dispute the content if they're not satisfied, but you'll get a chance to correct it based on their feedback — it's a fair process for both sides."
                        )
                    }}
                </div>
            </div>
            <div>
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-shield-check"
                        class="mr-1 text-warning"
                    />
                    {{ $t("Watch for high-dispute customers") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "If a customer abuses the dispute process, a warning will show on their profile that they have a high dispute rate. Feel free to reject any request from a customer with a bad dispute history."
                        )
                    }}
                </div>
            </div>
            <b-btn
                @click="guideModal = false"
                variant="primary"
                pill
                block
                class="mt-3"
            >
                {{ $t("Got it") }}
            </b-btn>
        </b-modal>
    </div>
</template>

<script>
import BackToDashboard from "@/components/utility/BackToDashboard";
import UserStandardAvatar from "@/components/user/UserStandardAvatar";
import CreatorRequestStatus from "@/components/creatorRequest/CreatorRequestStatus";
import { mapState } from "vuex";

export default {
    data() {
        return {
            creatorRequests: null,
            guideModal: false,
        };
    },
    mounted() {
        this.refresh();
    },
    computed: {
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
    },
    methods: {
        async enableRequests() {
            await this.api.post("/userFlag/creatorEnableRequests");
            this.$store.dispatch("onboarding/status");
        },
        async refresh() {
            const { data } = await this.api.post(
                "/creatorRequest/requestedOfCreator"
            );
            this.creatorRequests = data.creatorRequests;
        },
    },
    components: {
        BackToDashboard,
        UserStandardAvatar,
        CreatorRequestStatus,
    },
};
</script>
